import {RefObject, useEffect, useLayoutEffect, useRef} from 'react'

import {MOBILE_VIEWPORT_QUERY} from 'hooks/useMobileViewport'

interface UseMobilePaneFocusOptions {
  /** True while the pane is open. */
  active: boolean
  /** Usually the pane's back button. */
  targetRef: RefObject<HTMLElement>
  /** Returns focus to the opener when the pane closes. */
  restoreFocus?: boolean
  /** Decides, from what has focus, whether the pane may take it. */
  canTakeFocus?: (focused: Element | null) => boolean
}

/**
 * Phone panes move focus to their back button when they open and give it back
 * to whatever opened them when they close, so keyboard and screen reader
 * users never land on content that slid away. A field being edited inside the
 * pane (for example across a rotation) keeps its focus and keyboard.
 */
export function useMobilePaneFocus({
  active,
  targetRef,
  restoreFocus = true,
  canTakeFocus,
}: UseMobilePaneFocusOptions): void {
  const canTakeFocusRef = useRef(canTakeFocus)

  useLayoutEffect(() => {
    canTakeFocusRef.current = canTakeFocus
  }, [canTakeFocus])

  useEffect(() => {
    if (!active) return undefined
    const opener = document.activeElement as HTMLElement | null
    const pane = targetRef.current?.closest('[data-mobile-pane]')
    const wasEditingPane = Boolean(opener && pane?.contains(opener))

    const frame = requestAnimationFrame(() => {
      if (wasEditingPane) return
      const focused = document.activeElement
      const check = canTakeFocusRef.current
      // A fast tap into a field after opening owns focus over this transition.
      const mayTakeFocus = check
        ? check(focused)
        : focused === opener || focused === document.body
      if (mayTakeFocus) targetRef.current?.focus({preventScroll: true})
    })

    return () => {
      cancelAnimationFrame(frame)
      if (
        restoreFocus &&
        !wasEditingPane &&
        opener?.isConnected &&
        opener !== document.body &&
        !opener.closest('[inert], [aria-hidden="true"]') &&
        window.matchMedia(MOBILE_VIEWPORT_QUERY).matches
      ) {
        opener.focus({preventScroll: true})
      }
    }
  }, [active, restoreFocus, targetRef])
}
